import { LayoutGroup } from 'framer-motion';
import React, { useRef, useState } from 'react';

import type { MediaItem } from './MediaModal';

import { MediaGridItem } from './MediaGridItem';

import './MediaGrid.scss';

export interface MediaGridProps {
	/** The media items to display in the grid */
	items: MediaItem[];
	/** Called with the new item order during and after a drag-to-reorder operation */
	onItemsReordered: (items: MediaItem[]) => void;
	/** Called when a media item is clicked (passes 0-based index) */
	onItemClick?: (index: number) => void;
	/** Called when a media item is deleted (passes 0-based index) */
	onDeleteItem: (index: number) => void;
	/** Whether multi-select mode is active */
	isSelectMode: boolean;
	/** Set of selected item IDs */
	selectedIds: Set<string>;
	/** Called when a single item's selection state is toggled */
	onSelectionChange: (id: string) => void;
}

export const MediaGrid = ({
	items,
	onItemsReordered,
	onItemClick,
	onDeleteItem,
	isSelectMode,
	selectedIds,
	onSelectionChange,
}: MediaGridProps): React.JSX.Element => {
	const [draggingId, setDraggingId] = useState<string | null>(null);
	// Refs for synchronous drag-state access — avoids stale closure bugs when
	// dragenter fires faster than React can re-render and update state.
	const draggedIdRef = useRef<string | null>(null);
	const itemsRef = useRef(items);
	itemsRef.current = items;

	const handleDragStart = (id: string): void => {
		draggedIdRef.current = id;
		setDraggingId(id);
	};

	const handleDragOver = (targetId: string): void => {
		if (draggedIdRef.current === null || targetId === draggedIdRef.current) return;

		const currentItems = itemsRef.current;
		const from = currentItems.findIndex((item) => item.id === draggedIdRef.current);
		const to = currentItems.findIndex((item) => item.id === targetId);
		if (from === -1 || to === -1) return;

		const reordered = [...currentItems];
		reordered.splice(to, 0, reordered.splice(from, 1)[0]);
		onItemsReordered(reordered);
	};

	const handleDragEnd = (): void => {
		draggedIdRef.current = null;
		setDraggingId(null);
	};

	return (
		<div className='media-grid'>
			<LayoutGroup>
				{items.map((item, i) => (
					<MediaGridItem
						key={item.id}
						item={item}
						index={i + 1}
						isDragging={draggingId === item.id}
						onClick={() => onItemClick?.(i)}
						onDelete={() => onDeleteItem(i)}
						onDragStart={handleDragStart}
						onDragOver={handleDragOver}
						onDragEnd={handleDragEnd}
						isSelectMode={isSelectMode}
						isSelected={selectedIds.has(item.id)}
						onSelect={() => onSelectionChange(item.id)}
					/>
				))}
			</LayoutGroup>
		</div>
	);
};
